import * as React from "react";
import { ViewMedium, Viewport, ViewSmall } from "bernie-view";
import { LocalizedComponentClass, LocalizedComponentProps, withLocalization } from "bernie-l10n";
import { UitkIcon } from "uitk-react-icons";
import { AnalyticsStore } from "bernie-core";
import { useLocalization } from "@shared-ui/localization-context";

import { WizardHotelPWA } from "../WizardHotelPWA";
import { WizardHotelPWAProps } from "../typings";
import { sendDelayedTrackEvent, Action, FlexTrackingInfo } from "src/components/utility/analytics/FlexAnalyticsUtils";
import { inject } from "mobx-react";
import { UitkTertiaryButton } from "uitk-react-button";

const getTrackingConfig = (isExpanded: boolean): FlexTrackingInfo => {
  const key = isExpanded ? "open" : "close";

  return {
    moduleName: "wizard-hotel-pwa",
    action: Action.CLICK,
    linkName: `${key} wizard with expando`,
    rfrr: `expando.${key}`
  };
};

const useExpando = (defaultState: boolean, analytics?: AnalyticsStore) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultState);

  const toggle = React.useCallback(
    () =>
      setIsExpanded(isExp => {
        const nextState = !isExp;
        const trackInfo = getTrackingConfig(nextState);

        sendDelayedTrackEvent(trackInfo, analytics);

        return nextState;
      }),
    [setIsExpanded, analytics]
  );

  return {
    toggle,
    isExpanded
  };
};

export const WizardHotelPWAExpando = inject("analytics")((props: WizardHotelPWAProps) => {
  const { toggle, isExpanded } = useExpando(false, props.analytics);

  return (
    <Viewport>
      <ViewSmall>
        <div
          className="uitk-card uitk-flat-border-top uitk-flat-border-bottom uitk-cell"
          data-stid="section-hotel-search"
          id={props.flexComponent.id}
        >
          {isExpanded ? <OpenedWizard {...props} handleClose={toggle} /> : <ExpandoWizard handleClick={toggle} />}
        </div>
      </ViewSmall>
      <ViewMedium>
        <WizardHotelPWA {...props} />
      </ViewMedium>
    </Viewport>
  );
});

interface OpenedWizardProps extends LocalizedComponentProps, WizardHotelPWAProps {
  handleClose: () => void;
}

const OpenedWizard: React.FC<OpenedWizardProps> = props => {
  const { formatText } = useLocalization();

  return (
    <>
      <WizardHotelPWA {...props} />
      <div className="uitk-card uitk-flat-border-top uitk-flat-border-bottom all-b-padding-six">
        <UitkTertiaryButton
          onClick={props.handleClose}
          text={formatText("DestinationsByAffinity.priceDialogCloseButton")}
          isFullWidth={true}
        />
      </div>
    </>
  );
};

interface ExpandoWizardProps extends LocalizedComponentProps {
  handleClick: () => void;
}

const ExpandoWizard = withLocalization(({ l10n, ...props }: ExpandoWizardProps) => {
  const playbackSectionClasses =
    "uitk-link all-x-padding-three all-y-padding-three uitk-flex uitk-flex-align-items-center uitk-flex-justify-content-flex-start";

  const { formatText } = useLocalization();

  return (
    <div onClick={props.handleClick} className={playbackSectionClasses}>
      <UitkIcon
        className="all-r-margin-three"
        id="playback-icon"
        name="search"
        title={formatText("wizard.hotel.pwa.expand.icon.title")}
      />
      <div>
        <span className="uitk-type-500 uitk-type-medium truncate" data-stid="content-location-title">
          {formatText("wizard.hotel.pwa.expand.title")}
        </span>
        <div className="uitk-type-200">
          <span>{formatText("wizard.hotel.pwa.expand.subtitle")}</span>
        </div>
      </div>
    </div>
  );
}) as LocalizedComponentClass<ExpandoWizardProps>;

export default WizardHotelPWAExpando;
